import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  PrimaryCTAButton,
  VStack,
} from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import {
  HissaReportType,
  ScheduledReport,
  ScheduledReportRun,
  ScheduleType,
  UpdateScheduleRequest,
} from "../../../types/common";
import {
  useGetScheduledReports,
  useUpdateScheduledReport,
  useDeactivateScheduledReport,
  useDeleteScheduledReport,
  useGetScheduleRunHistory,
  useSkipScheduledReport,
} from "../../../queries/scheduledReports";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";
import { formatDisplayDate } from "../../../utils/date";
import { useFilteredAssociatedUsers } from "./ScheduleSection";

interface Props {
  reportType: HissaReportType;
  reportName: string;
  /**
   * For CUSTOM_REPORT, scopes the schedule list to a single saved custom
   * report (matched against `schedule.params.customReportId`). Ignored for
   * other report types since reportType alone uniquely identifies them.
   */
  customReportId?: string;
  onClose: () => void;
}

const ORDINAL_SUFFIXES: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
function ordinal(n: number): string {
  return `${n}${ORDINAL_SUFFIXES[n] ?? "th"}`;
}

function scheduleLabel(schedule: ScheduledReport): string {
  if (schedule.scheduleType === "MONTHLY_DATE" && schedule.monthlyDay) {
    return `${ordinal(schedule.monthlyDay)} of every month`;
  }
  const d = schedule.intervalDays ?? 0;
  return `Every ${d} day${d !== 1 ? "s" : ""}`;
}

function ScheduleRow({
  schedule,
  users,
}: {
  schedule: ScheduledReport;
  users: { id: string; name: string }[];
}) {
  const { mutate: update, isLoading: updating } = useUpdateScheduledReport();
  const { mutate: deactivate, isLoading: deactivating } =
    useDeactivateScheduledReport();
  const { mutate: deleteSchedule, isLoading: deleting } =
    useDeleteScheduledReport();
  const { mutate: skip, isLoading: skipping } = useSkipScheduledReport();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  // Edit state
  const [editLabel, setEditLabel] = useState(schedule.label ?? "");
  const [editScheduleType, setEditScheduleType] = useState<ScheduleType>(
    schedule.scheduleType
  );
  const [intervalDays, setIntervalDays] = useState<string>(
    String(schedule.intervalDays ?? 10)
  );
  const [monthlyDay, setMonthlyDay] = useState<string>(
    String(schedule.monthlyDay ?? 1)
  );
  const [selectedNames, setSelectedNames] = useState<string[]>(
    users.filter((u) => schedule.recipients.includes(u.id)).map((u) => u.name)
  );
  const [editStartDate, setEditStartDate] = useState<string>(
    String(schedule.params?.startDate ?? "")
  );

  const userOptions = users.map((u) => ({ value: u.name }));

  const { data: runHistory = [], isLoading: historyLoading } =
    useGetScheduleRunHistory(showHistory ? schedule.id : null);

  const recipientNames =
    users
      .filter((u) => schedule.recipients.includes(u.id))
      .map((u) => u.name)
      .join(", ") || "—";

  const handleToggleActive = () => {
    if (schedule.isActive) {
      deactivate(schedule.id);
    } else {
      update({ id: schedule.id, payload: { isActive: true } });
    }
  };

  const handleSave = () => {
    const ids = users
      .filter((u) => selectedNames.includes(u.name) && u.id)
      .map((u) => u.id);
    const payload: UpdateScheduleRequest = {
      recipients: ids,
      label: editLabel.trim(),
    };

    if (editScheduleType === "INTERVAL") {
      const parsed = parseInt(intervalDays, 10);
      payload.scheduleType = "INTERVAL";
      payload.intervalDays = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    } else {
      const parsed = parseInt(monthlyDay, 10);
      payload.scheduleType = "MONTHLY_DATE";
      payload.monthlyDay =
        Number.isNaN(parsed) || parsed < 1 ? 1 : Math.min(parsed, 31);
    }

    if (editStartDate) {
      payload.startDate = editStartDate;
    }

    update(
      { id: schedule.id, payload },
      { onSuccess: () => setEditing(false) }
    );
  };

  const nextRun = schedule.nextRunAt
    ? formatDisplayDate(new Date(schedule.nextRunAt))
    : "—";
  const lastRun = schedule.lastRunAt
    ? formatDisplayDate(new Date(schedule.lastRunAt))
    : "Never";

  return (
    <div className="border border-gray-100 rounded-lg p-4">
      {/* Header row */}
      <HStack className="items-center justify-between mb-3">
        <HStack className="items-center gap-2">
          <button
            type="button"
            onClick={handleToggleActive}
            disabled={updating || deactivating}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
              schedule.isActive ? "bg-orange-501" : "bg-gray-300"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                schedule.isActive ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          <span className="text-xs font-medium text-gray-600">
            {schedule.isActive ? "Active" : "Paused"}
          </span>
        </HStack>

        <HStack className="items-center gap-3">
          {!editing && (
            <>
              <button
                type="button"
                onClick={() => skip(schedule.id)}
                disabled={skipping || !schedule.isActive}
                title="Skip next run"
                className="text-xs text-gray-400 hover:text-orange-501 disabled:opacity-40 flex items-center gap-1"
              >
                <Icon icon="mdi:skip-next-outline" width={15} />
                Skip
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-orange-501 hover:underline"
              >
                Edit
              </button>
              {confirmDelete ? (
                <HStack className="items-center gap-1">
                  <span className="text-xs text-red-500">Delete?</span>
                  <button
                    type="button"
                    onClick={() => deleteSchedule(schedule.id)}
                    disabled={deleting}
                    className="text-xs text-red-500 font-semibold hover:underline"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs text-gray-400 hover:underline"
                  >
                    No
                  </button>
                </HStack>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  title="Delete schedule"
                  className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                >
                  <Icon icon="mdi:trash-can-outline" width={15} />
                </button>
              )}
            </>
          )}
        </HStack>
      </HStack>

      {/* Info / edit rows */}
      {editing ? (
        <VStack className="gap-3">
          <div>
            <Label className="text-xs font-normal">Label (optional)</Label>
            <Input
              type="text"
              placeholder="e.g. Monthly compliance report"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs font-normal">Recipients</Label>
            <MultiSelectV2
              _className="w-full"
              multiple
              placeholder="-- Select users --"
              options={userOptions}
              optionValues={selectedNames}
              handleSelection={setSelectedNames}
            />
          </div>

          {/* Schedule type */}
          <div>
            <Label className="text-xs font-normal">Schedule type</Label>
            <HStack className="gap-4 mt-1">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700">
                <input
                  type="radio"
                  name={`scheduleType-${schedule.id}`}
                  value="INTERVAL"
                  checked={editScheduleType === "INTERVAL"}
                  onChange={() => setEditScheduleType("INTERVAL")}
                  className="accent-orange-501"
                />
                Every N days
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700">
                <input
                  type="radio"
                  name={`scheduleType-${schedule.id}`}
                  value="MONTHLY_DATE"
                  checked={editScheduleType === "MONTHLY_DATE"}
                  onChange={() => setEditScheduleType("MONTHLY_DATE")}
                  className="accent-orange-501"
                />
                Day of every month
              </label>
            </HStack>
          </div>

          {editScheduleType === "INTERVAL" ? (
            <div className="w-56">
              <Label className="text-xs font-normal">Repeat every (days)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={intervalDays}
                onChange={(e) =>
                  setIntervalDays(e.target.value.replace(/[^0-9]/g, ""))
                }
                onBlur={() => {
                  const parsed = parseInt(intervalDays, 10);
                  setIntervalDays(
                    String(Number.isNaN(parsed) || parsed < 1 ? 1 : parsed)
                  );
                }}
              />
            </div>
          ) : (
            <div className="w-56">
              <Label className="text-xs font-normal">Day of month (1–31)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={monthlyDay}
                onChange={(e) =>
                  setMonthlyDay(e.target.value.replace(/[^0-9]/g, ""))
                }
                onBlur={() => {
                  const parsed = parseInt(monthlyDay, 10);
                  setMonthlyDay(
                    String(
                      Number.isNaN(parsed) || parsed < 1
                        ? 1
                        : Math.min(parsed, 31)
                    )
                  );
                }}
              />
            </div>
          )}
          <HStack className="gap-6 items-end">
            <VStack className="gap-1 w-56">
              <Label className="text-xs font-normal">
                Starting from&nbsp;
                <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Input
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
            </VStack>
            {editStartDate && (
              <button
                type="button"
                onClick={() => setEditStartDate("")}
                className="mb-1 text-xs text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            )}
          </HStack>

          <HStack className="gap-4 justify-between">
            <ButtonSecondary
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-gray-500 hover:underline"
            >
              Cancel
            </ButtonSecondary>
            <ButtonPrimary
              type="button"
              onClick={handleSave}
              disabled={updating || selectedNames.length === 0}
              className="text-xs font-medium text-white bg-orange-501 rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {updating ? "Saving…" : "Save"}
            </ButtonPrimary>
          </HStack>
        </VStack>
      ) : (
        <div>
          {schedule.label && (
            <div className="text-sm font-medium text-gray-700 mb-2">
              {schedule.label}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Recipients: </span>
              {recipientNames}
            </div>
            <div>
              <span className="text-gray-400">Runs: </span>
              {scheduleLabel(schedule)}
            </div>
            <div>
              <span className="text-gray-400">Last run: </span>
              {lastRun}
            </div>
            <div>
              <span className="text-gray-400">Next run: </span>
              {nextRun}
              {schedule.skippedDates && schedule.skippedDates.length > 0 && (
                <button
                  type="button"
                  className="ml-1 text-amber-500 text-xs hover:underline"
                  onClick={() => setShowSkipped((v) => !v)}
                >
                  ({schedule.skippedDates.length} skipped)
                </button>
              )}
            </div>
          </div>
          {showSkipped &&
            schedule.skippedDates &&
            schedule.skippedDates.length > 0 && (
              <div className="mt-1 ml-1 text-xs text-gray-500 space-y-0.5">
                {schedule.skippedDates.map((entry, idx) => {
                  const isObj = typeof entry !== "string";
                  const date = isObj ? entry.date : entry;
                  const skippedByName = isObj
                    ? entry.skippedByName ??
                      (entry.skippedBy
                        ? users.find((u) => u.id === entry.skippedBy)?.name ??
                          `User ${entry.skippedBy}`
                        : null)
                    : null;
                  const userName = skippedByName;
                  return (
                    <div key={idx}>
                      <span className="text-amber-600">
                        {new Date(date).toLocaleDateString()}
                      </span>
                      {userName && (
                        <span className="text-gray-400">
                          {" "}
                          : skipped by {userName}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          {/* Run history */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="text-xs text-gray-400 hover:text-orange-501 flex items-center gap-1"
            >
              <Icon
                icon={showHistory ? "mdi:chevron-up" : "mdi:chevron-down"}
                width={14}
              />
              {showHistory ? "Hide run history" : "Show run history"}
            </button>
            {showHistory && (
              <div className="mt-2 pl-1">
                {historyLoading ? (
                  <div className="text-xs text-gray-400">Loading…</div>
                ) : runHistory.length === 0 ? (
                  <div className="text-xs text-gray-400">No runs yet.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 text-left">
                        <th className="pr-3 font-normal">Status</th>
                        <th className="pr-3 font-normal">Started</th>
                        <th className="font-normal">Delivered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(runHistory as ScheduledReportRun[])
                        .slice(0, 5)
                        .map((run) => (
                          <tr key={run.id} className="border-t border-gray-50">
                            <td
                              className={`pr-3 py-1 font-medium ${
                                run.status === "SUCCESS"
                                  ? "text-green-600"
                                  : run.status === "FAILED"
                                  ? "text-red-500"
                                  : "text-gray-400"
                              }`}
                            >
                              {run.status}
                            </td>
                            <td className="pr-3 py-1 text-gray-500">
                              {formatDisplayDate(new Date(run.startedAt))}
                            </td>
                            <td className="py-1 text-gray-500">
                              {
                                run.deliveries.filter((d) => !d.failureReason)
                                  .length
                              }
                              /{run.deliveries.length}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ScheduleManagementDialog: React.FC<Props> = ({
  reportType,
  reportName,
  customReportId,
  onClose,
}) => {
  const { data: allSchedules = [], isLoading } = useGetScheduledReports();
  const { data: associatedUsers = [] } = useGetAssociatedUsers();

  const { data: _associatedUsers = [] } =
    useFilteredAssociatedUsers(associatedUsers);

  const users = _associatedUsers
    .filter((u) => u.userId)
    .map((u) => ({ id: String(u.userId), name: u.name }));
  const schedules = allSchedules.filter((s) => {
    if (s.reportType !== reportType) return false;
    if (reportType === HissaReportType.CUSTOM_REPORT && customReportId) {
      return (s.params as any)?.customReportId === customReportId;
    }
    return true;
  });

  return (
    <div className="w-[600px] mx-auto bg-white rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-6 text-lg font-medium border-b py-5">
        <div>
          <div>Scheduled Runs</div>
          <div className="text-sm font-normal text-gray-500 mt-0.5">
            {reportName}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-gray-500 hover:text-gray-800"
        >
          <Icon icon="mdi:close" width={20} />
        </button>
      </div>

      {/* Body */}
      <VStack className="p-6 gap-3 overflow-y-auto max-h-[480px]">
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading schedules…</div>
        ) : schedules.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
            <Icon icon="mdi:calendar-clock-outline" width={36} />
            <div className="text-sm">No schedules for this report.</div>
            <div className="text-xs text-gray-400">
              Click `Generate Report` and enable scheduling to add one.
            </div>
          </div>
        ) : (
          schedules.map((s) => (
            <ScheduleRow key={s.id} schedule={s} users={users} />
          ))
        )}
      </VStack>
    </div>
  );
};

export default ScheduleManagementDialog;
